import classNames from 'clsx';
import { ReactComponent as Icon20Swap } from 'img/icons/swap.svg';
import throttle from 'lodash.throttle';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';

import styles from './ImageCompare.css';

const maxLabelWidth = 100;

export const ImageCompare = memo<{
    imageUrl: string;
    imageNewUrl: string;
    scaleVal?: number | null;
}>(({ imageUrl, imageNewUrl, scaleVal }) => {
    const [split, setSplit] = useState(50);
    const [isMoving, setIsMoving] = useState<boolean | undefined>();
    const ref = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        sendDwhRadar({ t: 'compare', action: 'show', name: 'nmz_ai_improve' });
    }, []);

    const updateSplitPos = useCallback(
        (left: number, width: number) => {
            let newVal = Math.round((100 * left) / width);

            if (newVal < 0) {
                newVal = 0;
            }

            if (newVal > 100) {
                newVal = 100;
            }

            setSplit(newVal);
        },
        [setSplit]
    );

    const handleMouseMove = useCallback(
        throttle(
            (event: React.MouseEvent<HTMLInputElement>) => {
                event.stopPropagation();

                if (isMoving) {
                    const targetRect = event.currentTarget?.getBoundingClientRect?.();

                    if (!targetRect) {
                        return;
                    }

                    updateSplitPos(event.clientX - targetRect.left, targetRect.width);
                }
            },
            16,
            { trailing: true }
        ),
        [isMoving, updateSplitPos]
    );
    const handleMouseDown = useCallback((event) => {
        sendDwhRadar({ t: 'compare', action: 'click', name: 'nmz_ai_improve' });

        const targetRect = event.currentTarget?.getBoundingClientRect?.();

        if (!targetRect) {
            return;
        }

        setIsMoving(true);

        updateSplitPos(event.clientX - targetRect.left, targetRect.width);
    }, []);
    const handleMouseUp = useCallback(() => setIsMoving(false), []);

    const imageWidth = ref?.current?.getBoundingClientRect()?.width || 0;

    const { scaleRev, scale } = useMemo(() => {
        if (scaleVal) {
            return {
                scaleRev: { scale: (1 / scaleVal).toString() },
                scale: { scale: scaleVal.toString() },
            };
        }
        return { scaleRev: undefined, scale: undefined };
    }, [scaleVal]);

    return (
        <div
            className={classNames(styles.root, { [styles.hideUI]: isMoving })}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            data-qa-id="img-comare"
            style={{ ...scaleRev }}
        >
            <img src={imageUrl} alt="Before" className={styles.originalBefore} data-qa-id="img-comare-before" style={{ ...scale }} />
            {imageNewUrl && (
                <>
                    <div style={{ clipPath: `polygon(${split}% 0, 100% 0, 100% 100%, ${split}% 100%)` }} className={styles.newWrapper}>
                        <img
                            ref={ref}
                            src={imageNewUrl}
                            alt="After"
                            className={styles.newAfter}
                            data-qa-id="img-compare-after"
                            style={{ ...scale }}
                        />
                    </div>
                    <input type="range" min="0" max="100" value={split} className={styles.inputSlider} />
                    <div className={styles.slider} style={{ left: `${split}%` }}>
                        <div className={styles.thumb} data-qa-id="img-compare-slider">
                            <Icon20Swap />
                        </div>
                    </div>
                    <div
                        className={classNames(styles.label, styles.labelBefore, {
                            [styles.hideLabel]: imageWidth > 0 && (split * imageWidth) / 100 / (scaleVal || 1) < maxLabelWidth,
                        })}
                        style={{ left: `calc(${split}% - 24px - 18px)` }}
                        data-qa-id="img-compare-laber-before"
                    >
                        До
                    </div>
                    <div
                        className={classNames(styles.label, styles.labelAfter, {
                            [styles.hideLabel]: imageWidth > 0 && ((100 - split) * imageWidth) / 100 / (scaleVal || 1) < maxLabelWidth,
                        })}
                        style={{ left: `calc(${split}% + 24px + 18px)` }}
                        data-qa-id="img-compare-laber-after"
                    >
                        После
                    </div>
                </>
            )}
        </div>
    );
});

ImageCompare.displayName = 'ImageCompare';
